import React from 'react';
import { FileText, Download, CheckCircle } from 'lucide-react';

const Code = () => {
  const principles = [
    "Ethics and Professional Conduct",
    "Governance and Risk Management",
    "Execution Quality and Transparency",
    "Information Sharing and Confidentiality",
    "Compliance and Oversight",
    "Market Integrity and Fair Practices"
  ];

  const handleDownload = () => {
    // Mock download - in production this would download actual PDF
    alert('In production, this would download the complete FX Global Code PDF document.');
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 py-16">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-12">
          <span className="text-cyan-500 font-semibold text-sm uppercase tracking-wide">Global Standards</span>
          <h1 className="text-5xl font-bold text-gray-900 mt-2 mb-6">The FX Global Code</h1>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            A set of global principles of good practice in the foreign exchange market
          </p>
        </div>

        {/* Main Content */}
        <div className="bg-white rounded-2xl p-8 lg:p-12 shadow-lg mb-8">
          <div className="flex items-start space-x-4 mb-8">
            <div className="w-16 h-16 bg-gradient-to-br from-cyan-400 to-cyan-600 rounded-xl flex items-center justify-center shadow-lg flex-shrink-0">
              <FileText className="text-white" size={32} />
            </div>
            <div>
              <h2 className="text-3xl font-bold text-gray-900 mb-4">About The Code</h2>
              <p className="text-gray-600 text-lg leading-relaxed">
                This set of global principles of good practice in the foreign exchange market (Global Code) 
                has been developed to provide a common set of guidelines to promote the integrity and effective 
                functioning of the wholesale foreign exchange market (FX Market).
              </p>
            </div>
          </div>

          <div className="bg-gradient-to-br from-gray-50 to-gray-100 rounded-xl p-6 mb-8">
            <p className="text-gray-700 leading-relaxed mb-4">
              It is intended to promote a robust, fair, liquid, open, and appropriately transparent market in 
              which a diverse set of Market Participants, supported by resilient infrastructure, are able to 
              confidently and effectively transact at competitive prices that reflect available market information 
              and in a manner that conforms to acceptable standards of behaviour.
            </p>
            <p className="text-gray-700 leading-relaxed">
              The Global Code does not impose legal or regulatory obligations on Market Participants nor does 
              it substitute for regulation, but rather it is intended to serve as a supplement to any and all 
              local laws, rules, and regulations by identifying global good practices and processes.
            </p>
          </div>

          {/* Key Principles */}
          <div className="mb-8">
            <h3 className="text-2xl font-bold text-gray-900 mb-6">Key Principles</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {principles.map((principle, index) => (
                <div key={index} className="flex items-start space-x-3">
                  <CheckCircle className="text-cyan-500 flex-shrink-0 mt-1" size={20} />
                  <span className="text-gray-700">{principle}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Download Button */}
          <div className="bg-gradient-to-r from-cyan-500 to-blue-600 rounded-xl p-8 text-center">
            <h3 className="text-2xl font-bold text-white mb-4">Download Complete Code</h3>
            <p className="text-cyan-50 mb-6">Get the full FX Global Code document in PDF format</p>
            <button
              onClick={handleDownload}
              className="inline-flex items-center px-8 py-4 bg-white text-cyan-600 rounded-xl font-semibold shadow-lg hover:shadow-xl transition-all duration-200 space-x-2"
            >
              <Download size={20} />
              <span>Download PDF</span>
            </button>
          </div>
        </div>

        {/* Applicability Section */}
        <div className="bg-white rounded-2xl p-8 lg:p-12 shadow-lg">
          <h2 className="text-3xl font-bold text-gray-900 mb-6">Who Should Follow This Code?</h2>
          <p className="text-gray-600 text-lg leading-relaxed mb-6">
            The FX Market features a diverse set of participants who engage in the market in different ways 
            and across various FX products. The Global Code is written with this diversity in mind and is 
            expected to apply to all FX Market Participants that engage in the FX Markets, including:
          </p>
          <ul className="space-y-3">
            <li className="flex items-start space-x-3">
              <CheckCircle className="text-cyan-500 flex-shrink-0 mt-1" size={20} />
              <span className="text-gray-700">Sell-side and buy-side entities</span>
            </li>
            <li className="flex items-start space-x-3">
              <CheckCircle className="text-cyan-500 flex-shrink-0 mt-1" size={20} />
              <span className="text-gray-700">Non-bank liquidity providers</span>
            </li>
            <li className="flex items-start space-x-3">
              <CheckCircle className="text-cyan-500 flex-shrink-0 mt-1" size={20} />
              <span className="text-gray-700">Operators of E-Trading Platforms</span>
            </li>
            <li className="flex items-start space-x-3">
              <CheckCircle className="text-cyan-500 flex-shrink-0 mt-1" size={20} />
              <span className="text-gray-700">Entities providing brokerage, execution, and aggregation services</span>
            </li>
          </ul>
        </div>
      </div>
    </div>
  );
};

export default Code;